import {
  BREW_STAGES,
  type BrewStage,
  dec,
  laterStage,
  toCanonical,
  UNITS,
  whirlpoolSteps,
} from "@bl/domain";
import type { BrewDetail, BrewPatch } from "../api";
import { clearLocal, readLocal } from "../autosave";
import {
  amountOf,
  type BrewLine,
  celsiusOf,
  type DraftPull,
  fahrenheitOf,
  inUnit,
  lotKey,
  lotsOf,
  NONE,
  shown,
  unitOf,
  unitOfPull,
} from "../brewPulls";
import { STAGE_LABELS } from "../components/BrewPills";

/**
 * Brew day (PLAN.md, "Slice 2"), built for a phone first (Tyler, 2026-10-01): one column of cards, the
 * number keypad wherever a number goes in, the phone's own pickers for the date and each lot, and the
 * buttons always at the foot of the screen. The page runs in the day's order: pull the ingredients,
 * take the readings, then mark it brewed, which is the only point anything leaves inventory.
 */

const LITERS_PER_GALLON = "3.785411784";
export interface Draft {
  brewedOn: string;
  strikeTempF: string;
  mashTempF: string;
  mashPh: string;
  preBoilGal: string;
  preBoilGravity: string;
  og: string;
  fermenterGal: string;
  /**
   * By whirlpool step, under its first hop's line: as typed, and empty for a step that takes the first
   * step's (Tyler, 2026-10-19).
   */
  whirlpoolF: Record<string, string>;
  notes: string;
  /** The stage the brewer said, by tapping it on the strip; null to go by the readings. */
  stage: Stage | null;
  /** By recipe line. */
  pulls: Record<string, DraftPull[]>;
}
export type Stage = BrewStage;
/** The day's stages in order, named as the header's pill names them. */
export const STAGES: { id: Stage; label: string }[] = BREW_STAGES.map((id) => ({
  id,
  label: STAGE_LABELS[id],
}));
/** The stage the readings typed so far reach. */
export function typedStage(d: Draft): Stage {
  return d.og || d.fermenterGal
    ? "fermenter"
    : d.preBoilGal || d.preBoilGravity || Object.values(d.whirlpoolF).some(Boolean)
      ? "boil"
      : d.strikeTempF || d.mashTempF || d.mashPh
        ? "mash"
        : "pull";
}
/**
 * Where the brew has got to: the stage the brewer said by tapping it, or further on if what has been
 * typed says so, the same reading of it as the header's pill (`brewStage`), so the page opens where the
 * brewer is (the critique, 2026-10-06: "the page has no idea of the current stage").
 */
export function stageOf(d: Draft): Stage {
  const typed = typedStage(d);
  return laterStage(d.stage, typed) ?? typed;
}
/** A line's pulls as the folded list reads them: "11 lb from P-01", "15 lb of the G1 harvest". */
export function pulledLine(line: BrewLine, pulls: DraftPull[]): string {
  const lots = lotsOf(line);
  return (
    pulls
      .filter((p) => amountOf(p.amount) > 0)
      .map((p) => {
        const lot = lots.find((o) => lotKey(o) === p.lotKey);
        const from =
          p.lotKey === NONE
            ? ", not in inventory"
            : lot?.generation != null
              ? ` of the G${lot.generation} harvest`
              : lot
                ? ` from ${lot.lotCode ?? lot.locationName}`
                : "";
        return `${shown(amountOf(p.amount))} ${UNITS[unitOfPull(line, p.lotKey)].label}${from}`;
      })
      .join(", ") || "Nothing set"
  );
}
const figure = (v: string | null, places = 3) => (v === null ? "" : shown(Number(v), places));
export const gallons = (liters: string | number | null, places = 2) =>
  liters === null
    ? ""
    : shown(Number(dec(String(liters)).div(dec(LITERS_PER_GALLON)).toFixed(6)), places);
const fahrenheit = (celsius: string | null) =>
  celsius === null ? "" : shown(Number(fahrenheitOf(celsius)), 1);
/**
 * The recipe's whirlpool hops in steps, each the hops that go in together, hottest first
 * (`whirlpoolSteps`): brew day asks how hot the wort was for each (Scott's Jam, 2026-10-08).
 */
export function whirlpoolStepsOf(b: BrewDetail): BrewLine[][] {
  return whirlpoolSteps(
    b.lines
      .filter((l) => l.kind === "hop" && l.hopUse === "whirlpool")
      .map((line) => ({
        id: line.recipeLineId,
        tempC: line.tempC === null ? null : Number(line.tempC),
        minutes: Number(line.minutes ?? 0),
        line,
      })),
  ).map((step) => step.map((s) => s.line));
}
/** Where a step's temperature is kept in the draft: under its first hop's line. */
export const stepKey = (step: BrewLine[]) => step[0]?.recipeLineId ?? "";
function draftOf(b: BrewDetail): Draft {
  return {
    brewedOn: b.brewedOn,
    strikeTempF: fahrenheit(b.readings.strikeTempC),
    mashTempF: fahrenheit(b.readings.mashTempC),
    mashPh: figure(b.readings.mashPh, 2),
    preBoilGal: gallons(b.readings.preBoilLiters),
    preBoilGravity: figure(b.readings.preBoilGravity),
    og: figure(b.readings.og),
    fermenterGal: gallons(b.readings.fermenterLiters),
    whirlpoolF: Object.fromEntries(
      whirlpoolStepsOf(b).map((step) => {
        const typed = b.readings.whirlpoolTemps.find((w) =>
          step.some((l) => l.recipeLineId === w.recipeLineId),
        );
        return [stepKey(step), fahrenheit(typed?.tempC ?? null)];
      }),
    ),
    notes: b.notes ?? "",
    stage: b.stage,
    pulls: Object.fromEntries(
      b.lines.map((l) => [
        l.recipeLineId,
        l.pulls.map((p) => ({
          key: p.id,
          lotKey: lotKey(p),
          amount: inUnit(p.quantity, p.unit),
        })),
      ]),
    ),
  };
}
/** A reading as the API takes it, or null when nothing (or nothing readable) is typed. */
function reading(value: string, from: "gallons" | "fahrenheit" | null = null): string | null {
  const n = Number(value);
  if (value.trim() === "" || !Number.isFinite(n) || n <= 0) return null;
  if (from === "gallons") return dec(String(n)).times(dec(LITERS_PER_GALLON)).toFixed(6);
  if (from === "fahrenheit") return celsiusOf(String(n));
  return String(n);
}
export function patchOf(b: BrewDetail, d: Draft): BrewPatch {
  // No final gravity: it is not brew day's (Scott, 2026-10-02), and one already recorded stays.
  const patch: BrewPatch = {
    strikeTempC: reading(d.strikeTempF, "fahrenheit"),
    mashTempC: reading(d.mashTempF, "fahrenheit"),
    mashPh: reading(d.mashPh),
    preBoilLiters: reading(d.preBoilGal, "gallons"),
    preBoilGravity: reading(d.preBoilGravity),
    og: reading(d.og),
    fermenterLiters: reading(d.fermenterGal, "gallons"),
    // Each step's on every hop of it; a step left empty takes the first step's, on the server too.
    whirlpoolTemps: whirlpoolStepsOf(b).flatMap((step) => {
      const tempC = reading(d.whirlpoolF[stepKey(step)] ?? "", "fahrenheit");
      return tempC === null ? [] : step.map((l) => ({ recipeLineId: l.recipeLineId, tempC }));
    }),
    notes: d.notes.trim() || null,
  };
  // The day and the pulls are fixed once it is brewed: they are what left inventory.
  if (b.status === "draft") {
    if (d.brewedOn) patch.brewedOn = d.brewedOn;
    patch.stage = d.stage;
    patch.pulls = b.lines.flatMap((l) => {
      const item = l.item;
      if (!item) return [];
      const lots = lotsOf(l);
      return (d.pulls[l.recipeLineId] ?? [])
        .filter((p) => p.lotKey && amountOf(p.amount) > 0)
        .map((p) => {
          // Not in inventory has no lot, and Brewed records it to assign later.
          const [lotId = "", locationId = ""] = p.lotKey === NONE ? [] : p.lotKey.split("|");
          // A yeast's harvest is its own item, weighed: the lot says which and how.
          const lot = lots.find((o) => lotKey(o) === p.lotKey);
          return {
            recipeLineId: l.recipeLineId,
            itemId: lot?.itemId ?? item.id,
            lotId: lotId || null,
            locationId: locationId || null,
            quantity: toCanonical(
              { value: String(amountOf(p.amount)), unit: lot?.unit ?? unitOf(l) },
              lot?.dimension ?? item.dimension,
            ),
          };
        });
    });
  }
  return patch;
}
export const localKeyOf = (brewId: string) => `bl.brew.${brewId}`;
/**
 * Where the page starts: what this device typed and never got to the server, if it is newer than the
 * server's last autosave, else the brew as the server has it.
 */
export function startingDraft(b: BrewDetail): { draft: Draft; unsent: boolean } {
  const local = readLocal<Draft>(localKeyOf(b.id));
  if (local && (!b.autosavedAt || Date.parse(local.savedAt) > Date.parse(b.autosavedAt))) {
    // Kept before a reading was added to the page, it has none of that reading.
    return { draft: { ...draftOf(b), ...local.value }, unsent: true };
  }
  // The server has it, or something newer: a save sent as the page closed lands without the page
  // living to clear its copy.
  if (local) clearLocal(localKeyOf(b.id));
  return { draft: draftOf(b), unsent: false };
}
